/**
 * Ed25519 sender-signature helpers for Kaether outbound envelopes.
 *
 * Every outbound encrypted envelope is signed with the sender's long-term
 * Ed25519 identity key.  Receivers MUST verify the signature before
 * attempting AES-GCM decryption.  A signature failure means the envelope
 * did not originate from the claimed sender — the caller must refuse to
 * render and surface a hard error.
 *
 * ## Browser floor
 * Ed25519 in Web Crypto (`{ name: "Ed25519" }`) is supported in:
 *   - Chrome 130+
 *   - Firefox 130+
 *   - Safari 17+
 * This module will throw `SignatureAlgorithmError` on browsers that do not
 * support the algorithm.
 *
 * ## iOS wire-format parity
 * The canonical bytes layout (see `canonicalBytes`) matches the field order
 * used by `kaether_for_ios/Stoat/E2EE/SignalCrypto.swift`.  The iOS encrypt
 * path signs the same concatenation; the iOS decrypt path verifies it.
 *
 * Pure logic / SubtleCrypto only.  No SolidJS imports.
 */

// ─── constants ───────────────────────────────────────────────────────────────

/** Ed25519 signature is always exactly 64 bytes. */
export const ED25519_SIG_BYTES = 64;

/** Ed25519 public key is always exactly 32 bytes. */
export const ED25519_PUB_KEY_BYTES = 32;

// ─── error types ─────────────────────────────────────────────────────────────

/**
 * Thrown when signature verification fails (bad key, tampered bytes, etc.).
 * Callers must not fall through to decryption after catching this.
 */
export class SignatureVerificationError extends Error {
  constructor(detail: string) {
    super(`[SenderSignatures] Signature verification failed: ${detail}`);
    this.name = "SignatureVerificationError";
  }
}

/**
 * Thrown when the underlying Web Crypto API rejects the Ed25519 algorithm
 * (unsupported browser) or when a key of the wrong type is passed.
 */
export class SignatureAlgorithmError extends Error {
  constructor(detail: string) {
    super(`[SenderSignatures] Algorithm error: ${detail}`);
    this.name = "SignatureAlgorithmError";
  }
}

// ─── key import helpers ───────────────────────────────────────────────────────

const ALG = { name: "Ed25519" } as const;

/**
 * Import a raw 32-byte Ed25519 public key for verification.
 */
export async function importPublicKey(
  rawBytes: Uint8Array,
): Promise<CryptoKey> {
  if (rawBytes.byteLength !== ED25519_PUB_KEY_BYTES) {
    throw new SignatureAlgorithmError(
      `Expected ${ED25519_PUB_KEY_BYTES}-byte public key, got ${rawBytes.byteLength}`,
    );
  }
  try {
    return await crypto.subtle.importKey("raw", rawBytes, ALG, true, [
      "verify",
    ]);
  } catch (err) {
    throw new SignatureAlgorithmError(
      `importKey (public) failed — browser may not support Ed25519: ${String(err)}`,
    );
  }
}

/**
 * Import a raw 32-byte Ed25519 private key for signing.
 *
 * Note: WebCrypto represents the Ed25519 private key as the 32-byte seed
 * (the "raw" private key), not the 64-byte expanded form used in some libs.
 */
export async function importPrivateKey(
  rawBytes: Uint8Array,
): Promise<CryptoKey> {
  try {
    return await crypto.subtle.importKey("raw", rawBytes, ALG, false, [
      "sign",
    ]);
  } catch (err) {
    throw new SignatureAlgorithmError(
      `importKey (private) failed — browser may not support Ed25519: ${String(err)}`,
    );
  }
}

/**
 * Generate a fresh Ed25519 keypair (for tests and bootstrapping).
 * Returns the public key as extractable so tests can read the raw bytes.
 */
export async function generateKeyPair(): Promise<{
  publicKey: CryptoKey;
  privateKey: CryptoKey;
  publicKeyBytes: Uint8Array;
}> {
  const kp = await crypto.subtle.generateKey(ALG, true, ["sign", "verify"]);
  const pubBuf = await crypto.subtle.exportKey("raw", kp.publicKey);
  return {
    publicKey: kp.publicKey,
    privateKey: kp.privateKey,
    publicKeyBytes: new Uint8Array(pubBuf),
  };
}

// ─── canonical bytes ──────────────────────────────────────────────────────────

/**
 * Parameters that describe one side of an envelope (sender or recipient).
 */
export interface EnvelopeParty {
  /** User-ID string, UTF-8 encoded on the wire. */
  userId: string;
  /** Device ID, encoded as 4-byte big-endian uint32. */
  deviceId: number;
}

/**
 * The minimum set of envelope fields required to produce canonical bytes.
 */
export interface EnvelopeFields {
  sender: EnvelopeParty;
  recipient: EnvelopeParty;
  /** Raw AES-GCM ciphertext (nonce ‖ tag ‖ data or nonce ‖ data ‖ tag,
   *  whichever the outer layer produces — must be stable).          */
  ciphertext: Uint8Array;
  /** Additional authenticated data, or empty Uint8Array if not used. */
  aad: Uint8Array;
}

const enc = new TextEncoder();

/** Write a 4-byte big-endian uint32 into `buf` at `offset`. */
function writeU32BE(buf: Uint8Array, offset: number, value: number): void {
  buf[offset] = (value >>> 24) & 0xff;
  buf[offset + 1] = (value >>> 16) & 0xff;
  buf[offset + 2] = (value >>> 8) & 0xff;
  buf[offset + 3] = value & 0xff;
}

/**
 * Produce the deterministic canonical byte representation of an envelope that
 * is fed into Ed25519 sign / verify.
 *
 * ## Byte layout (matches iOS SignalCrypto.swift)
 *
 * ```
 * [ 4 bytes ]  len(senderUserId_utf8)          — big-endian uint32
 * [ N bytes ]  senderUserId_utf8
 * [ 4 bytes ]  senderDeviceId                  — big-endian uint32
 * [ 4 bytes ]  len(recipientUserId_utf8)        — big-endian uint32
 * [ N bytes ]  recipientUserId_utf8
 * [ 4 bytes ]  recipientDeviceId               — big-endian uint32
 * [ 4 bytes ]  len(ciphertext)                  — big-endian uint32
 * [ N bytes ]  ciphertext
 * [ 4 bytes ]  len(aad)                         — big-endian uint32
 * [ N bytes ]  aad
 * ```
 *
 * The signature field itself is NOT included (you can't sign the thing that
 * contains the signature of itself).  Every variable-length field is prefixed
 * with a 4-byte big-endian length so:
 *  - empty payloads produce distinct canonical bytes from absent payloads,
 *  - field boundaries cannot be shifted by embedding length-like bytes in data.
 */
export function canonicalBytes(fields: EnvelopeFields): Uint8Array {
  const senderUserIdBytes = enc.encode(fields.sender.userId);
  const recipientUserIdBytes = enc.encode(fields.recipient.userId);

  // Compute total length up-front so we do a single allocation.
  const totalLen =
    4 +
    senderUserIdBytes.byteLength +
    4 + // senderDeviceId
    4 +
    recipientUserIdBytes.byteLength +
    4 + // recipientDeviceId
    4 +
    fields.ciphertext.byteLength +
    4 +
    fields.aad.byteLength;

  const buf = new Uint8Array(totalLen);
  let pos = 0;

  // senderUserId
  writeU32BE(buf, pos, senderUserIdBytes.byteLength);
  pos += 4;
  buf.set(senderUserIdBytes, pos);
  pos += senderUserIdBytes.byteLength;

  // senderDeviceId
  writeU32BE(buf, pos, fields.sender.deviceId);
  pos += 4;

  // recipientUserId
  writeU32BE(buf, pos, recipientUserIdBytes.byteLength);
  pos += 4;
  buf.set(recipientUserIdBytes, pos);
  pos += recipientUserIdBytes.byteLength;

  // recipientDeviceId
  writeU32BE(buf, pos, fields.recipient.deviceId);
  pos += 4;

  // ciphertext
  writeU32BE(buf, pos, fields.ciphertext.byteLength);
  pos += 4;
  buf.set(fields.ciphertext, pos);
  pos += fields.ciphertext.byteLength;

  // aad
  writeU32BE(buf, pos, fields.aad.byteLength);
  pos += 4;
  buf.set(fields.aad, pos);

  return buf;
}

// ─── sign / verify ────────────────────────────────────────────────────────────

/**
 * Sign the canonical bytes of `fields` with `privateKey`.
 *
 * Returns a 64-byte Ed25519 signature.
 */
export async function signEnvelope(
  fields: EnvelopeFields,
  privateKey: CryptoKey,
): Promise<Uint8Array> {
  const canonical = canonicalBytes(fields);
  let sigBuf: ArrayBuffer;
  try {
    sigBuf = await crypto.subtle.sign(ALG, privateKey, canonical);
  } catch (err) {
    throw new SignatureAlgorithmError(
      `sign() failed — key may be wrong algorithm: ${String(err)}`,
    );
  }
  const sig = new Uint8Array(sigBuf);
  if (sig.byteLength !== ED25519_SIG_BYTES) {
    // This should never happen with a well-implemented WebCrypto, but guard
    // anyway so tests catch broken polyfills.
    throw new SignatureAlgorithmError(
      `Unexpected signature length: ${sig.byteLength} (expected ${ED25519_SIG_BYTES})`,
    );
  }
  return sig;
}

/**
 * Verify that `signature` was produced by the private key corresponding to
 * `publicKey` over the canonical bytes of `fields`.
 *
 * Returns `true` if valid.  Returns `false` if the signature is
 * cryptographically invalid (wrong key, tampered payload, etc.).
 *
 * Throws `SignatureAlgorithmError` only on irrecoverable crypto errors
 * (e.g. the public key is from a different algorithm, or the browser does not
 * support Ed25519).
 *
 * Callers MUST NOT proceed to AES-GCM decryption if this returns `false`.
 */
export async function verifyEnvelope(
  fields: EnvelopeFields,
  signature: Uint8Array,
  publicKey: CryptoKey,
): Promise<boolean> {
  const canonical = canonicalBytes(fields);
  let ok: boolean;
  try {
    ok = await crypto.subtle.verify(ALG, publicKey, signature, canonical);
  } catch (err) {
    // WebCrypto throws (rather than returning false) when the key algorithm
    // doesn't match or data is malformed beyond recovery.  Map to
    // SignatureAlgorithmError so callers can distinguish "wrong algorithm"
    // from "valid algorithm, bad signature".
    throw new SignatureAlgorithmError(
      `verify() threw — check that the public key is Ed25519: ${String(err)}`,
    );
  }
  return ok;
}

/**
 * Convenience wrapper: verify and throw `SignatureVerificationError` when the
 * result is false.  Use this at the decryption entry point so callers can
 * treat both "algorithm error" and "bad signature" uniformly with a single
 * try/catch.
 */
export async function verifyEnvelopeOrThrow(
  fields: EnvelopeFields,
  signature: Uint8Array,
  publicKey: CryptoKey,
): Promise<void> {
  const ok = await verifyEnvelope(fields, signature, publicKey);
  if (!ok) {
    throw new SignatureVerificationError(
      "signature did not match; envelope may be tampered or from wrong sender",
    );
  }
}
